import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Shield, 
  Droplets, 
  Car, 
  CheckCircle,
  Sparkles,
  Award,
  Zap,
  Sun,
  Cpu
} from "lucide-react";
import xpelLogo from "@/assets/xpel-logo.png";

const TeslaProtection = () => {

  const teslaFeatures = [
    {
      icon: Droplets,
      title: "Soft Paint Composition",
      description: "Tesla's water-based paint is thinner and softer than traditional automotive paint, requiring specialized protection."
    },
    {
      icon: Sparkles,
      title: "Prone to Swirl Marks",
      description: "Even light washing can cause micro-scratches visible in sunlight—PPF prevents contact damage."
    },
    {
      icon: Zap,
      title: "Rock Chip Vulnerability",
      description: "Front-end damage occurs faster due to paint softness. Self-healing PPF absorbs impacts."
    },
    {
      icon: Award,
      title: "Resale Value Protection",
      description: "Tesla owners expect pristine paint. Protection maintains trade-in value and buyer confidence."
    }
  ];

  const packages = [
    {
      name: "Essential",
      subtitle: "Full Front",
      startingPrice: "$1,500",
      coverage: ["Hood", "Front bumper", "Fenders", "Mirror caps", "Headlights"],
      ideal: "Budget-conscious protection",
      badge: null
    },
    {
      name: "Daily Driver",
      subtitle: "Full Front + Rockers",
      startingPrice: "$2,500",
      coverage: ["Everything in Essential", "Rocker panels", "A-pillars", "Door edges"],
      ideal: "Daily commuters on I-15/215",
      badge: "Most Popular"
    },
    {
      name: "Stealth",
      subtitle: "Full Matte Conversion",
      startingPrice: "$6,500",
      coverage: ["Full body coverage", "XPEL Stealth film", "Satin finish transformation", "Complete protection"],
      ideal: "Transform to a matte finish",
      badge: "Premium"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Tesla PPF Las Vegas | Protection for Model 3, Y, S, X | Battle Born</title>
        <meta 
          name="description" 
          content="Specialized paint protection film and ceramic coating packages for Tesla vehicles in Las Vegas. Expert installation for soft Tesla paint. Free quote." 
        />
        <meta property="og:title" content="Tesla PPF Las Vegas | Battle Born Protection Films" />
        <meta property="og:description" content="The ultimate protection for your Tesla. Specialized PPF packages for soft Tesla paint." />
        <link rel="canonical" href="https://battlebornppf.com/tesla-protection" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Tesla Paint Protection Film",
            "provider": {
              "@type": "AutomotiveBusiness",
              "name": "Battle Born Protection Films",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "6960 W Russell Rd B",
                "addressLocality": "Las Vegas",
                "addressRegion": "NV",
                "postalCode": "89113",
                "addressCountry": "US"
              }
            },
            "areaServed": "Las Vegas, NV",
            "description": "Specialized paint protection film packages for Tesla Model 3, Y, S, X, and Cybertruck."
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center bg-gradient-to-b from-black via-zinc-900 to-background overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-accent/10 via-transparent to-transparent" />
        <div className="container mx-auto px-4 py-20 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-accent/10 border border-accent/30 rounded-full px-4 py-2 mb-6">
            <Zap className="w-4 h-4 text-accent" />
            <span className="text-sm text-accent font-medium">Tesla Specialists</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground mb-6 leading-tight">
            The Ultimate Protection for Your{" "}
            <span className="text-accent">Tesla</span> in Las Vegas
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Specialized PPF & Ceramic Packages for Soft Tesla Paint
          </p>
          
          <Button 
            asChild 
            variant="hero" 
            size="lg" 
            className="min-h-[48px] text-base"
          >
            <Link to="/contact">Get Your Tesla Quote</Link>
          </Button>
        </div>
      </section>

      {/* Why Teslas Need Special Care */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Why Teslas Need <span className="text-accent">Special Care</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Tesla's innovative paint technology requires specialized protection to maintain its showroom finish.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {teslaFeatures.map((feature, index) => (
              <div 
                key={index}
                className="flex gap-4 p-6 bg-card rounded-lg border border-white/10"
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                    <feature.icon className="w-6 h-6 text-accent" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Tesla Owners Need PPF — Deep Dive */}
      <section className="py-20 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-8 text-center">
              Why Tesla Owners Need PPF
            </h2>
            <div className="space-y-6 text-muted-foreground text-lg leading-relaxed">
              <p>
                Tesla uses a water-based paint system across its entire lineup — an environmentally conscious choice, but one that comes with a significant trade-off. Tesla paint measures just <strong className="text-foreground">3–4 mils thick</strong> compared to the <strong className="text-foreground">5–6 mils</strong> found on traditional luxury vehicles from BMW, Mercedes, and Porsche. That 30–40% reduction in thickness means less clear coat protecting the color layer, making Tesla paint notoriously vulnerable to rock chips, scratches, and swirl marks from everyday driving and washing.
              </p>
              <p>
                This is especially problematic for <strong className="text-foreground">Model 3</strong> and <strong className="text-foreground">Model Y</strong> owners, who represent the majority of Teslas on Las Vegas roads. These vehicles are daily drivers commuting on highways like I-15, US-95, and the 215 Beltway — routes littered with construction debris, gravel, and kicked-up sand. Without PPF, the front end of a Model 3 or Y can accumulate dozens of visible rock chips within the first few months of ownership. Many Tesla owners discover the paint problem only after it's too late, facing thousands in paint correction or respray costs.
              </p>
              <p>
                Paint Protection Film solves this problem completely. XPEL Ultimate Plus PPF adds <strong className="text-foreground">8 mils of self-healing urethane</strong> to your Tesla's painted surfaces — more than doubling the total protection thickness. The film absorbs rock impacts, heals surface scratches with heat from the sun, and blocks UV radiation that accelerates fading and oxidation. For Tesla owners, PPF isn't optional — it's the most important investment you can make after purchasing the vehicle itself.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Model-Specific Coverage */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <Car className="w-16 h-16 text-accent mx-auto mb-6" />
              <h2 className="text-4xl font-heading font-bold mb-4">
                Model-Specific <span className="text-accent">Coverage Recommendations</span>
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Every Tesla model has unique high-impact zones. Here's what we recommend for each.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-2xl font-heading flex items-center gap-3">
                    <Zap className="w-6 h-6 text-accent" />
                    Model 3
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">The best-selling Tesla and most common on Las Vegas roads. The low front end catches maximum road debris.</p>
                  <ul className="space-y-2">
                    {["Full hood & front bumper (high-impact zone)", "Fenders & mirror caps", "Rocker panels (road salt & debris)", "Door edges & handle cups", "Rear bumper loading area (cargo scratches)", "Headlights & fog lights"].map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-2xl font-heading flex items-center gap-3">
                    <Zap className="w-6 h-6 text-accent" />
                    Model Y
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">Same soft paint as Model 3, with additional surfaces exposed by the SUV profile.</p>
                  <ul className="space-y-2">
                    {["Everything recommended for Model 3", "Rear hatch edge (loading zone)", "B-pillar piano black trim", "Extended rocker panels (larger surface area)", "Rear quarter panels", "Tailgate lip & lower bumper"].map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-2xl font-heading flex items-center gap-3">
                    <Zap className="w-6 h-6 text-accent" />
                    Model S
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">Tesla's flagship sedan with sweeping body lines that benefit from comprehensive coverage.</p>
                  <ul className="space-y-2">
                    {["Full front end (hood, bumper, fenders)", "Side skirts & rocker panels", "Rear diffuser area", "Door edges & trunk lip", "Mirror caps & A-pillars", "Headlights & front air intakes"].map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-2xl font-heading flex items-center gap-3">
                    <Zap className="w-6 h-6 text-accent" />
                    Model X
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">Falcon Wing doors create unique wear points that require specialized protection templates.</p>
                  <ul className="space-y-2">
                    {["Full front bumper & hood", "Falcon Wing door edges (critical)", "Rocker panels & wheel arches", "Front fenders & mirror caps", "Lower body cladding", "Rear bumper & liftgate edges"].map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Tesla-Specific Challenges */}
      <section className="py-20 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Sun className="w-16 h-16 text-accent mx-auto mb-6" />
              <h2 className="text-4xl font-heading font-bold mb-4">
                Tesla-Specific <span className="text-accent">Challenges</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <h3 className="text-xl font-heading font-bold mb-3">Glass Roof UV Exposure</h3>
                  <p className="text-muted-foreground">
                    Tesla's panoramic glass roof spans from windshield to rear window. While it filters UV, the interior still absorbs significant heat and light. Ceramic coating on exterior glass reduces heat transmission and repels water for improved visibility during Las Vegas monsoon storms.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <h3 className="text-xl font-heading font-bold mb-3">Piano Black Trim</h3>
                  <p className="text-muted-foreground">
                    Tesla uses glossy piano black trim on center consoles, B-pillars, and interior surfaces. This material shows fingerprints, swirl marks, and micro-scratches instantly. PPF applied to piano black trim prevents scratching and keeps these surfaces looking factory-new.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <h3 className="text-xl font-heading font-bold mb-3">White Interior Protection</h3>
                  <p className="text-muted-foreground">
                    Tesla's popular white vegan leather interior looks stunning but is prone to dye transfer from jeans, staining from sunscreen, and UV yellowing. Ceramic coating on interior surfaces creates an invisible barrier against stains while making cleanup effortless.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <h3 className="text-xl font-heading font-bold mb-3">Charging Port Area</h3>
                  <p className="text-muted-foreground">
                    The charging port door and surrounding paint see daily contact and potential cable scuffing. A small PPF kit on the charging port area prevents scratches and paint wear from the repetitive plugging and unplugging that comes with EV ownership.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* PPF Packages */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Tesla Protection <span className="text-accent">Packages</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Choose the level of protection that fits your driving lifestyle.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {packages.map((pkg, index) => (
              <Card 
                key={index}
                className="relative bg-[#2A2A2A] border-white/10 overflow-hidden group hover:border-accent/50 transition-all duration-300"
              >
                {pkg.badge && (
                  <div className="absolute top-4 right-4 bg-accent text-accent-foreground text-xs font-bold px-3 py-1 rounded-full">
                    {pkg.badge}
                  </div>
                )}
                <CardHeader className="pb-4">
                  <CardTitle className="text-2xl font-heading text-foreground">
                    {pkg.name}
                  </CardTitle>
                  <p className="text-accent font-medium">{pkg.subtitle}</p>
                  <p className="text-2xl font-heading font-bold text-foreground mt-2">
                    Starting at {pkg.startingPrice}
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2">
                    {pkg.coverage.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="pt-4 border-t border-white/10">
                    <p className="text-xs text-muted-foreground">
                      <span className="text-accent">Ideal for:</span> {pkg.ideal}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-center text-sm text-muted-foreground mt-8 max-w-xl mx-auto">
            *Prices vary by model and condition. Contact us for an exact quote for your Tesla.
          </p>
        </div>
      </section>

      {/* Ceramic Coating for Tesla */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Sparkles className="w-16 h-16 text-accent mx-auto mb-6" />
              <h2 className="text-4xl font-heading font-bold mb-4">
                Ceramic Coating for <span className="text-accent">Tesla</span>
              </h2>
            </div>
            <div className="space-y-6 text-muted-foreground text-lg leading-relaxed">
              <p>
                While PPF provides physical impact protection, ceramic coating delivers the chemical and environmental defense your Tesla needs in the Las Vegas desert. <strong className="text-foreground">XPEL FUSION PLUS™</strong> ceramic coating creates a hydrophobic nano-ceramic layer that repels water, dust, and contaminants — keeping your Tesla cleaner between washes and dramatically reducing the effort required to maintain that showroom shine.
              </p>
              <p>
                Tesla's panoramic glass roof panels benefit enormously from ceramic coating. The coating repels rain and reduces water spotting from sprinkler overspray, while its UV-resistant properties help reduce interior heat buildup. We also apply ceramic coating to wheels and calipers — Tesla's regenerative braking system still produces brake dust, and a coated wheel surface prevents dust from bonding, making cleanup a simple rinse rather than scrubbing.
              </p>
              <p>
                For maximum protection, we recommend <strong className="text-foreground">PPF on high-impact areas combined with ceramic coating over the entire vehicle</strong> — including PPF-covered panels. This combination gives you physical protection where it matters most and hydrophobic, UV-resistant coverage everywhere else. It's the gold standard for Tesla owners who want comprehensive, long-lasting protection.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* XPEL DAP Templates */}
      <section className="py-20 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-12">
              <div className="flex-shrink-0">
                <div className="w-24 h-24 bg-accent/10 rounded-2xl flex items-center justify-center">
                  <Cpu className="w-12 h-12 text-accent" />
                </div>
              </div>
              <div>
                <h2 className="text-3xl font-heading font-bold mb-4">
                  Precision-Cut with XPEL DAP Software
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed mb-4">
                  Every Tesla PPF installation at Battle Born uses XPEL's proprietary <strong className="text-foreground">Design Access Program (DAP)</strong> — the industry's most comprehensive database of precision-cut templates. DAP contains exact patterns for every Tesla model, year, and trim level, ensuring each piece of film fits your vehicle's contours with factory-level precision.
                </p>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Computer-cut templates eliminate the need for hand-cutting film on your paint, reducing the risk of blade marks and ensuring consistent, repeatable results across every panel. This is one of the key advantages of choosing an XPEL-certified installer — access to DAP patterns that generic shops simply don't have.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-16 bg-[#1A1A1A] border-y border-white/10">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-heading font-bold text-accent mb-1">
                1,000+
              </div>
              <p className="text-sm text-muted-foreground">Teslas Protected</p>
            </div>
            
            <div className="h-12 w-px bg-white/10 hidden md:block" />
            
            <div className="flex items-center gap-3">
              <img 
                src={xpelLogo} 
                alt="XPEL Certified Installer" 
                className="h-10 w-auto"
                loading="lazy"
              />
              <div>
                <p className="text-sm font-medium text-foreground">XPEL Certified</p>
                <p className="text-xs text-muted-foreground">Installer</p>
              </div>
            </div>
            
            <div className="h-12 w-px bg-white/10 hidden md:block" />
            
            <div className="flex items-center gap-3">
              <Car className="w-8 h-8 text-accent" />
              <div>
                <p className="text-sm font-medium text-foreground">Soft Paint</p>
                <p className="text-xs text-muted-foreground">Specialists</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-b from-[#1A1A1A] to-background">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
            Ready to Protect Your <span className="text-accent">Tesla</span>?
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-8">
            Get a custom quote for your specific model and coverage needs. Our Tesla specialists are ready to help.
          </p>
          <Button 
            asChild 
            variant="premium" 
            size="lg"
            className="min-h-[48px]"
          >
            <Link to="/contact">Request Free Tesla Quote</Link>
          </Button>
        </div>
      </section>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background/95 backdrop-blur-sm border-t border-white/10 lg:hidden z-50">
        <Button 
          asChild 
          variant="hero" 
          className="w-full min-h-[56px] text-base"
        >
          <Link to="/contact">Get Your Tesla Quote</Link>
        </Button>
      </div>

      {/* Spacer for sticky CTA on mobile */}
      <div className="h-24 lg:hidden" />
    </>
  );
};

export default TeslaProtection;
